import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../_services/auth.service';
import { PermissionService } from '../_services/permission.service';
import { ToastrService } from 'ngx-toastr';

/** A `resource:action` pair a route may accept in place of a role name. */
export interface RoutePermission {
  resource: string;
  action: string;
}

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {

  constructor(
    private router: Router,
    private authService: AuthService,
    private permissionService: PermissionService,
    private toastr: ToastrService
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {

    // First check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    // Get allowed roles from route data
    const allowedRoles = next.data['allowedRoles'] as string[] || [];
    // Permissions that also open the route, for access granted per-role through RBAC rather than
    // by role name — delegated user management being the first (SPAR-745). OR'd with the roles
    // above, so a route listing only roles behaves exactly as it did before.
    const allowedPermissions = next.data['allowedPermissions'] as RoutePermission[] || [];

    // If neither is specified, deny access (configuration error)
    if (allowedRoles.length === 0 && allowedPermissions.length === 0) {
      this.toastr.warning('Access configuration error');
      this.router.navigate([this.authService.getHomeUrl()]);
      return false;
    }

    // Check if user has any of the allowed roles
    if (allowedRoles.length > 0 && this.authService.hasAnyRole(allowedRoles)) {
      return true;
    }

    if (allowedPermissions.some(p => this.permissionService.hasPermission(p.resource, p.action))) {
      return true;
    }

    // User doesn't have required role - redirect to their home page
    this.toastr.warning('You do not have permission to access this page');
    this.router.navigate([this.authService.getHomeUrl()]);
    return false;
  }
}

